import { SharedConfigurations, sleep } from '@pulse/common';
import { PulseEnvironment } from '../../globals';
import { PulseResponse } from '../../models';
import { DateService } from '../date.service';
import { DeviceIdService } from '../device-id.service';
import { DocumentVisibilityTracker } from '../visibility.tracker';

export class HttpService {
    private get async() {
        return this.documentVisibility.visible;
    }

    private readonly timeoutLimit = 20_000;

    constructor(
        private documentVisibility: DocumentVisibilityTracker,
        private env: PulseEnvironment,
        private date: DateService,
        private config: SharedConfigurations,
        private deviceIdService: DeviceIdService,
    ) {}

    async get<T = unknown>(
        url: string,
        headers: Record<string, string>,
        priority?: string,
    ): Promise<PulseResponse<T>> {
        return this.retry(
            (): Promise<PulseResponse<T>> => this.trySendGetRequest(url, headers, priority),
            this.config.transportRetries,
            this.config.transportTimeout,
        );
    }

    async post<T = unknown>(
        url: string,
        headers: Record<string, string>,
        data: any,
        keepalive = false,
    ): Promise<PulseResponse<T> | boolean> {
        return await this.retry(
            (): Promise<PulseResponse<T> | boolean> =>
                this.trySendPostRequest(url, headers, keepalive, data),
            this.config.transportRetries,
            this.config.transportTimeout,
        );
    }

    async put<T = unknown>(
        url: string,
        headers: Record<string, string>,
        data: any,
    ): Promise<PulseResponse<T>> {
        const response = await this.env.win.fetch(url, {
            method: 'PUT',
            headers,
            body: JSON.stringify(data),
        });

        return response.json();
    }

    async delete<T = unknown>(
        url: string,
        headers: Record<string, string>,
    ): Promise<PulseResponse<T>> {
        const response = await this.env.win.fetch(url, {
            method: 'DELETE',
            headers,
        });

        return response.json();
    }

    private async retry<T>(
        operation: () => Promise<T>,
        maxRetries: number = Number.MAX_SAFE_INTEGER,
        timeout = 60000,
    ): Promise<T> {
        const startTime = this.date.now();
        let retries = 0;

        while (true) {
            const timedOut = this.date.now() - startTime > timeout;
            const shouldRetry = retries < maxRetries;

            if (timedOut) {
                throw new Error('Timedout!');
            }

            if (!shouldRetry) {
                throw new Error('Max retries exeeded!');
            }

            if (!this.env.navigator.onLine && this.env.win.navigator.onLine !== undefined) {
                await this.waitForNetwork();
            }

            try {
                const resp = await operation();

                if (resp) {
                    return resp;
                }
                throw new Error('Request Failed!');
            } catch {
                const backoff = this.backoffTime(++retries);
                await sleep(backoff);
            }
        }
    }

    private waitForNetwork(): Promise<void> {
        return new Promise((resolve) => {
            if (this.env.win.navigator.onLine) {
                return resolve();
            }

            this.env.win.addEventListener('online', () => {
                resolve();
            });
        });
    }

    private succeeded(status: number | boolean) {
        // Intentionally treated 400 as success case
        // to skip request retry on client validation errors
        if (typeof status === 'boolean') {
            return status;
        }

        return status >= 200 && status <= 400;
    }

    private backoffTime(failures: number): number {
        if (!this.async && failures < 10) {
            return 0;
        }

        const backoff = Math.floor(
            Math.pow(failures + 1, failures + 1) * this.config.transportRetryBackoffMS,
        );

        return Math.min(this.config.transportRetryBackoffMaxMS, backoff);
    }

    private fetchPossible() {
        const win = this.env.win;

        const keepaliveSupported =
            'Request' in win && typeof new Request('https://t.picsart.com').keepalive === 'boolean';

        const supported = typeof win.fetch === 'function' && 'AbortController' in win;

        if (!supported || (!this.async && !keepaliveSupported)) {
            return false;
        }

        return true;
    }

    private sendBeacon(jsonData: string, url: string): boolean {
        return this.env.win.navigator.sendBeacon(`${url}/beacon`, jsonData);
    }

    private async trySendGetRequest<T = unknown>(
        url: string,
        headers: Record<string, string>,
        priority?: string,
    ): Promise<PulseResponse<T>> {
        let response: PulseResponse<T>;

        if (!this.env.win.navigator.onLine && this.env.win.navigator.onLine !== undefined) {
            await this.waitForNetwork();
        }

        if (this.fetchPossible()) {
            this.env.win.performance.mark('pulse_settingFetchRequestStart');
            const abortController = new AbortController();
            const id = setTimeout(() => abortController.abort(), this.timeoutLimit);
            const options: RequestInit & { priority?: string } = {
                method: 'GET',
                headers,
                credentials: 'include',
                signal: abortController.signal,
            };

            if (priority === 'high') {
                options.priority = priority;
            }
            let fetchResponse: Response;
            try {
                fetchResponse = await this.env.win.fetch(url, options);
            } catch (ex) {
                this.env.win.performance.mark('pulse_settingFetchRequestFailed');
                this.env.win.performance.measure(
                    'pulse_settingFetchRequestFailed',
                    'pulse_settingFetchRequestStart',
                    'pulse_settingFetchRequestFailed',
                );
                console.error('Fetch request failed:', {
                    url,
                    errorObject: ex,
                });
                throw ex;
            }
            this.env.win.performance.mark('pulse_settingFetchRequestSuccess');
            this.env.win.performance.measure(
                'pulse_settingFetchRequestSuccess',
                'pulse_settingFetchRequestStart',
                'pulse_settingFetchRequestSuccess',
            );
            clearTimeout(id);
            if (!fetchResponse) {
                return {
                    ok: false,
                    body: undefined as unknown as T,
                    headers: new Map(),
                    status: 400,
                };
            }

            const res = await fetchResponse?.json();
            const resHeaders: Map<string, string> = new Map(
                (fetchResponse.headers as any).entries(),
            );
            response = {
                ok: fetchResponse.ok,
                body: res,
                headers: resHeaders,
                status: fetchResponse?.status,
            };

            if (typeof response === 'object' && response?.headers.has('paa-did')) {
                this.replaceDeviceId(response.headers.get('paa-did'));
            }
        } else {
            response = await this.sendGetXHR(url, headers, true);
        }

        return response;
    }

    private async trySendPostRequest<T = unknown>(
        url: string,
        headers: Record<string, string>,
        keepalive: boolean,
        data: any,
    ): Promise<PulseResponse<T> | boolean> {
        let response: PulseResponse<T> | boolean;

        if (!this.env.win.navigator.onLine && this.env.win.navigator.onLine !== undefined) {
            await this.waitForNetwork();
        }

        if (this.fetchPossible()) {
            let fetchResponse;
            let resHeaders: Map<string, string>;
            let req;

            try {
                fetchResponse = await this.env.win.fetch(`${url}/fetch`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(data),
                    credentials: 'include',
                    keepalive,
                });
                resHeaders = new Map((fetchResponse.headers as any).entries());
                req = await fetchResponse.json();
                if (fetchResponse.status >= 500) {
                    return false;
                }
                response = {
                    ok: fetchResponse.status === 4,
                    body: req.body,
                    headers: resHeaders,
                    status: fetchResponse.status,
                };
            } catch {
                return false;
            }
            if (typeof response === 'object' && response?.headers.has('paa-did')) {
                this.replaceDeviceId(response.headers.get('paa-did'));
            }
        } else if (keepalive && typeof this.env.win.navigator?.sendBeacon === 'function') {
            const queued = this.sendBeacon(JSON.stringify(data), url);

            if (!queued) {
                response = await this.sendPostXHR(JSON.stringify(data), true, headers, url);
            } else {
                return true;
            }
        } else {
            response = await this.sendPostXHR(JSON.stringify(data), true, headers, url);
        }

        return response;
    }

    private async sendGetXHR<T = unknown>(
        url: string,
        headers: Record<string, string>,
        async: boolean,
    ): Promise<PulseResponse<T>> {
        this.env.win.performance.mark('pulse_settingXHRRequestStart');
        const env = this.env;
        const config = this.config;
        const response = new Promise<PulseResponse<T>>((resolve, reject) => {
            try {
                const req = new env.win.XMLHttpRequest();

                if (async) {
                    req.timeout = config.transportTimeout;
                    req.onreadystatechange = () => {
                        this.env.win.performance.mark('pulse_settingXHRRequestSuccess');
                        this.env.win.performance.measure(
                            'pulse_settingXHRRequestSuccess',
                            'pulse_settingXHRRequestStart',
                            'pulse_settingXHRRequestSuccess',
                        );
                        this.requestStateChange(req, resolve);
                    };
                }
                req.open('GET', `${url}`, async);
                req.withCredentials = true;
                req.timeout = this.timeoutLimit;
                for (const header in headers) {
                    req.setRequestHeader(header, String(headers[header]));
                }
                req.send();
            } catch (err) {
                this.env.win.performance.mark('pulse_settingXHRRequestFailed');
                this.env.win.performance.measure(
                    'pulse_settingXHRRequestFailed',
                    'pulse_settingXHRRequestStart',
                    'pulse_settingXHRRequestFailed',
                );
                reject(err);
            }
        });

        return await response;
    }

    private sendPostXHR<T>(
        jsonData: string,
        async: boolean,
        headers: Record<string, string>,
        url: string,
    ): Promise<PulseResponse<T>> {
        return new Promise<PulseResponse<T>>((resolve, reject) => {
            try {
                const req = new this.env.win.XMLHttpRequest();

                if (async) {
                    req.timeout = this.config.transportTimeout;
                    req.onreadystatechange = () => {
                        this.requestStateChange(req, resolve);
                    };
                }

                req.open('POST', `${url}/xhr`, async);
                req.withCredentials = true;

                for (const header in headers) {
                    req.setRequestHeader(header, String(headers[header]));
                }

                req.send(jsonData);

                if (!async) {
                    resolve(req.response);
                }
            } catch (ex) {
                reject(ex as string);
            }
        });
    }

    // TODO: Consider the reject case if response not succeeded
    private requestStateChange<T = unknown>(
        req: XMLHttpRequest,
        resolve: (value: PulseResponse<T> | PromiseLike<PulseResponse<T>>) => void,
    ): void {
        if (req.readyState === 4) {
            const ok = this.succeeded(req.status);
            const headers: Map<string, string> = new Map();
            req.getAllResponseHeaders()
                .trim()
                .split(/[\r\n]+/)
                .forEach((value, key) => {
                    headers.set(key.toString(), value);
                });
            let responseBody;
            try {
                responseBody = JSON.parse(req.response);
            } catch {
                throw new Error(`${req.response} is not a JSON!`);
            }
            const response: PulseResponse<T> = {
                ok,
                status: req.status,
                body: responseBody,
                headers: headers as Map<string, string>,
            };

            if (typeof response === 'object' && response?.headers.has('paa-did')) {
                this.replaceDeviceId(headers.get('paa-did'));
            }
            resolve(response);
        }
    }

    private replaceDeviceId(deviceId: string | undefined) {
        // Replace device id with the one found in the `paa-did`
        // response header to support cross-site tracking.
        if (deviceId) {
            this.deviceIdService.setDeviceId(deviceId);
        }
    }
}
